import { LucideIcon, type LucideNode } from '@kerfjs/ui/lucide-icon';
import type { SafeHtml } from 'kerfjs';
import {
  ArrowRight,
  ArrowUpDown,
  Bell,
  Blend,
  Bookmark,
  Bot,
  Check,
  ChevronDown,
  ChevronLeft,
  Circle,
  CircleCheck,
  CircleDot,
  Clock,
  Crosshair,
  Database,
  Download,
  Ellipsis,
  Flag,
  Funnel,
  Grid2x2,
  Key,
  PanelLeftClose,
  PanelLeftOpen,
  Pause,
  Pencil,
  Play,
  Plus,
  RotateCw,
  Search,
  Settings,
  Share2,
  Shield,
  Star,
  Trash2,
  TriangleAlert,
  Upload,
  X,
} from 'lucide';

/**
 * Every icon in the UI, by the app's own name for it (kerf UI migration,
 * NM-D5TYNV).
 *
 * Icons are Lucide nodes rendered through kerf UI's `LucideIcon`, which owns
 * the SVG markup, sizing and accessibility — this module only maps the app's
 * vocabulary onto Lucide's, so a call site says what it means (`'solo'`) rather
 * than which glyph happens to draw it (`CircleDot`). The `lucide` package is
 * tree-shaken: only the nodes imported here reach the bundle.
 *
 * No emoji and no hand-drawn glyphs — with one exception, the Apple mark for
 * Sign in with Apple (NM-252), which Lucide does not carry because it is a
 * brand logo. It is expressed as a node too, so it renders through the same
 * component.
 */
export type IconName =
  | 'check'
  | 'pause'
  | 'play'
  | 'plus'
  | 'solo'
  | 'delete'
  | 'clear'
  | 'settings'
  | 'rail-hide'
  | 'rail-show'
  | 'ok'
  | 'warn'
  | 'arrow'
  | 'download'
  | 'upload'
  | 'bookmark'
  | 'share'
  | 'star'
  | 'search'
  | 'guidance'
  | 'flag'
  | 'shield'
  | 'clock'
  | 'key'
  | 'bell'
  | 'database'
  | 'bot'
  | 'grid'
  | 'funnel'
  | 'blend'
  | 'pencil'
  | 'chevron'
  | 'chevron-left'
  | 'more'
  | 'sort'
  | 'step-done'
  | 'step-current'
  | 'step-ahead'
  | 'apple';

/** The Apple logo (NM-252): a filled glyph, so it sets its own fill and no stroke. */
const APPLE: LucideNode = [
  [
    'path',
    {
      fill: 'currentColor',
      stroke: 'none',
      d: 'M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.63-2.323-7.28 0-4.28 2.797-6.55 5.552-6.55 1.448 0 2.675.95 3.6.95.865 0 2.222-1.01 3.902-1.01.613 0 2.886.06 4.374 2.19-.13.09-2.383 1.37-2.383 4.19 0 3.26 2.854 4.42 2.955 4.45z',
    },
  ],
];

const NODES: Record<IconName, LucideNode> = {
  check: RotateCw, // "check now" — a refresh, not a tick
  pause: Pause,
  play: Play,
  plus: Plus,
  solo: CircleDot,
  delete: Trash2,
  clear: X,
  settings: Settings,
  // The Topics rail toggle (NM-614VD8): kerf UI's standard per-side glyphs
  // (`collapsiblePanelToggleIcon('left', …)`) — Close while open, Open while collapsed.
  'rail-hide': PanelLeftClose,
  'rail-show': PanelLeftOpen,
  ok: Check,
  warn: TriangleAlert,
  arrow: ArrowRight,
  download: Download,
  upload: Upload,
  bookmark: Bookmark,
  share: Share2,
  star: Star,
  search: Search,
  guidance: Crosshair, // a topic narrowed to what the user actually wants
  flag: Flag,
  shield: Shield,
  clock: Clock,
  key: Key,
  bell: Bell,
  database: Database,
  bot: Bot,
  grid: Grid2x2, // browsing a set of topics (NEWS-362)
  funnel: Funnel,
  blend: Blend,
  pencil: Pencil,
  chevron: ChevronDown,
  'chevron-left': ChevronLeft,
  more: Ellipsis,
  sort: ArrowUpDown,
  // The setup guide's step markers (NM-QJFW31): seen, here, not yet.
  'step-done': CircleCheck,
  'step-current': CircleDot,
  'step-ahead': Circle,
  apple: APPLE,
};

/** The Lucide node for an app icon name — for components that take `icon` as a node. */
export function iconNode(name: IconName): LucideNode {
  return NODES[name];
}

/**
 * Render an icon. Decorative (`aria-hidden`) — always paired with a text label
 * or an accessible name on its control. `size` is in CSS pixels; omit it to
 * take the surrounding font size (1em), which is what kerf UI components expect
 * of an icon placed in their `icon` slot.
 */
export function icon(name: IconName, size?: number): SafeHtml {
  return <LucideIcon icon={NODES[name]} name={name} size={size} />;
}
